import { useState } from 'react';
import { Seg, Toggle } from '../../ui/Block';
import { STRATEGIES, type SimConfig, type WorldDc, type WorldGpu } from './engine';
import { GpuGroups } from './GpuGroups';

export function groupPerH(b: SimConfig['balancer'], i = 0): number {
  const g = b.gpuTypes[i];
  if (!g) return 0;
  if (!g.gpus?.length) return g.perH;
  return Math.max(0, ...g.gpus.map((m) => b.gpus?.find((t) => t.name === m)?.perH ?? 0));
}

function WorldGpus({ cfg, set }: { cfg: SimConfig; set: (c: SimConfig) => void }) {
  const b = cfg.balancer;
  const names = [...new Set(b.gpuTypes.flatMap((g) => (g.gpus?.length ? g.gpus : [g.name])))];
  const dcs = [...new Set([...(b.dataCenters ?? []), ...(b.fallbackDataCenters ?? [])])];
  const wg = (n: string): WorldGpu => cfg.world.gpus?.find((g) => g.name === n) ?? { name: n, freeP: 1, brokenHostP: null, bootScale: 1 };
  const wd = (id: string): WorldDc => cfg.world.dataCenters?.find((d) => d.id === id) ?? { id, freeP: 1, rttMs: 0 };
  const setG = (g: WorldGpu) => set({ ...cfg, world: { ...cfg.world, gpus: [...(cfg.world.gpus ?? []).filter((x) => x.name !== g.name), g] } });
  const setD = (d: WorldDc) => set({ ...cfg, world: { ...cfg.world, dataCenters: [...(cfg.world.dataCenters ?? []).filter((x) => x.id !== d.id), d] } });
  const num = (label: string, v: number, on: (v: number) => void, pct = false) => (
    <label key={label} className="field" style={{ margin: 0 }}>
      <span className="field__label">{label}{pct ? ' (%)' : ''}</span>
      <input className="field__input mono" type="number" value={Number((pct ? v * 100 : v).toFixed(2))} onChange={(e) => { const x = Number(e.target.value); if (Number.isFinite(x)) on(pct ? Math.max(0, Math.min(1, x / 100)) : Math.max(0, x)); }} />
    </label>
  );
  return (
    <div style={{ minWidth: 0, display: 'grid', gap: 10 }}>
      <div className="tbl__h">SIMULATED RUNPOD, BY TYPE AND DATA CENTRE</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
        {names.flatMap((n) => {
          const g = wg(n);
          return [
            num(`${n}: free`, g.freeP, (v) => setG({ ...g, freeP: v }), true),
            num(`${n}: broken hosts`, g.brokenHostP ?? cfg.world.brokenHostP, (v) => setG({ ...g, brokenHostP: v }), true),
          ];
        })}
        {dcs.flatMap((id) => {
          const d = wd(id);
          return [num(`${id}: free`, d.freeP, (v) => setD({ ...d, freeP: v }), true), num(`${id}: RTT ms`, d.rttMs, (v) => setD({ ...d, rttMs: v }))];
        })}
      </div>
    </div>
  );
}

type Num = { label: string; get: (c: SimConfig) => number; set: (c: SimConfig, v: number) => SimConfig; step?: number; hint?: string; pct?: boolean };

const bal = (f: (b: SimConfig['balancer'], v: number) => Partial<SimConfig['balancer']>) => (c: SimConfig, v: number): SimConfig => ({ ...c, balancer: { ...c.balancer, ...f(c.balancer, v) } });
const tr = (k: keyof SimConfig['traffic']) => (c: SimConfig, v: number): SimConfig => ({ ...c, traffic: { ...c.traffic, [k]: v } });
const pr = (k: keyof SimConfig['pricing']) => (c: SimConfig, v: number): SimConfig => ({ ...c, pricing: { ...c.pricing, [k]: v } });
const wo = (k: keyof SimConfig['world']) => (c: SimConfig, v: number): SimConfig => ({ ...c, world: { ...c.world, [k]: v } });

export const POOL: Num[] = [
  { label: 'Most pods', get: (c) => c.balancer.maxPods, set: bal((_, v) => ({ maxPods: Math.round(v) })) },
  { label: 'Always-on pods', get: (c) => c.balancer.minPods ?? 0, set: bal((_, v) => ({ minPods: Math.max(0, Math.round(v)) })), hint: 'kept up with no traffic; 0 = off' },
  { label: 'Fly machines', get: (c) => c.balancer.flyPool, set: bal((_, v) => ({ flyPool: Math.max(1, Math.round(v)) })) },
  { label: 'Spend cap per day ($)', get: (c) => c.balancer.spendCapPerDay, set: bal((_, v) => ({ spendCapPerDay: v })) },
  { label: 'Fly headroom', get: (c) => c.balancer.flyHeadroomShare, set: bal((_, v) => ({ flyHeadroomShare: Math.max(0, v) })), pct: true, hint: 'of the pool; fewer free starts a pod at once (0 = off)' },
  { label: 'Turn-away starts a pod for (s)', get: (c) => c.balancer.rejectPressureS, set: bal((_, v) => ({ rejectPressureS: Math.max(0, v) })), hint: '0 = off' },
  { label: 'Pods kept stopped', get: (c) => c.balancer.keepStopped, set: bal((_, v) => ({ keepStopped: Math.round(v) })), hint: 'disk only, for a fast resume' },
];
export const RULES: Num[] = [
  { label: 'Settle (s)', get: (c) => c.balancer.settleS, set: bal((_, v) => ({ settleS: v })), hint: 'before a session counts or moves' },
  { label: 'Start window (s)', get: (c) => c.balancer.startWindowS, set: bal((_, v) => ({ startWindowS: v })) },
  { label: 'Start margin', get: (c) => c.balancer.startMargin, set: bal((_, v) => ({ startMargin: v })), step: 0.1, hint: 'over break-even' },
  { label: 'Drain margin', get: (c) => c.balancer.drainMargin, set: bal((_, v) => ({ drainMargin: v })), step: 0.1, hint: 'under break-even' },
  { label: 'Drain window (s)', get: (c) => c.balancer.drainWindowS, set: bal((_, v) => ({ drainWindowS: v })) },
  { label: 'Drain grace (s)', get: (c) => c.balancer.drainGraceS, set: bal((_, v) => ({ drainGraceS: v })), hint: 'before moving back to Fly' },
  { label: 'Empty pod stops after (s)', get: (c) => c.balancer.idleStopS, set: bal((_, v) => ({ idleStopS: v })) },
  { label: 'Moves in flight per pod', get: (c) => c.balancer.maxMovesPerPod, set: bal((_, v) => ({ maxMovesPerPod: Math.max(1, Math.round(v)) })) },
  { label: 'Heartbeat timeout (s)', get: (c) => c.balancer.heartbeatTimeoutS, set: bal((_, v) => ({ heartbeatTimeoutS: v })) },
  { label: 'No container after (s)', get: (c) => c.balancer.containerTimeoutS, set: bal((_, v) => ({ containerTimeoutS: v })) },
];
export const SPEECH: Num[] = [
  { label: 'Speech kept after the last ask (s)', get: (c) => c.balancer.speechIdleS ?? 60, set: bal((_, v) => ({ speechIdleS: Math.max(0, v) })), hint: 'a pod started for voices goes then' },
];

export function SpeechBrake({ cfg, set }: { cfg: SimConfig; set: (c: SimConfig) => void }) {
  const field = (k: 'speechPodHPerH' | 'speechPodHPerDay', label: string, hint: string) => {
    const v = cfg.balancer[k];
    return (
      <label key={k} className="field" style={{ margin: 0 }}>
        <span className="field__label">{label}</span>
        <input className="field__input mono" type="number" step={0.5} min={0} placeholder="no cap" value={v == null ? '' : String(v)}
          onChange={(e) => { const t = e.target.value.trim(); const x = Number(t); set({ ...cfg, balancer: { ...cfg.balancer, [k]: t === '' || !Number.isFinite(x) ? null : Math.max(0, x) } }); }} />
        <span className="field__hint">{hint}</span>
      </label>
    );
  };
  return (
    <div style={{ minWidth: 0 }}>
      <div className="tbl__h" style={{ marginBottom: 8 }}>EMERGENCY BRAKE ON VOICES (OFF UNLESS SET)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
        {field('speechPodHPerH', 'Voices alone: pod-hours an hour', 'blank = no cap; past it no pod starts or stays for voices')}
        {field('speechPodHPerDay', 'Voices alone: pod-hours a day', 'blank = no cap; live sessions\u2019 voices never count')}
      </div>
    </div>
  );
}
export const PAUSE: Num[] = [
  { label: 'Pauses per file, at most', get: (c) => c.balancer.pauseMaxPerFile, set: bal((_, v) => ({ pauseMaxPerFile: Math.max(0, Math.round(v)) })) },
  { label: 'Never in its last (s)', get: (c) => c.balancer.pauseMinLeftS, set: bal((_, v) => ({ pauseMinLeftS: Math.max(0, v) })), hint: 'engine seconds left' },
  { label: 'Never past', get: (c) => c.balancer.pauseMaxDone, set: bal((_, v) => ({ pauseMaxDone: Math.max(0, Math.min(1, v)) })), pct: true, hint: 'of the file done' },
  { label: 'A paused file waits at most (s)', get: (c) => c.balancer.pauseMaxWaitS, set: bal((_, v) => ({ pauseMaxWaitS: Math.max(0, v) })), hint: 'then the next place is its, and no file is paused' },
  { label: 'Live waits for a pod (s)', get: (c) => c.balancer.liveWaitS, set: bal((_, v) => ({ liveWaitS: Math.max(0, v) })), hint: 'one about to be ready; 0 = never' },
  { label: 'Redone on a resume (s)', get: (c) => c.world.pauseRedoS, set: wo('pauseRedoS'), hint: 'engine time, from the last committed chunk' },
];
const TRAFFIC: Num[] = [
  { label: 'Arrivals per hour', get: (c) => c.traffic.arrivalsPerH, set: tr('arrivalsPerH'), hint: 'at the day curve’s 1.0' },
  { label: 'Live share', get: (c) => c.traffic.liveShare, set: tr('liveShare'), pct: true },
  { label: 'English share', get: (c) => c.traffic.englishShare, set: tr('englishShare'), pct: true },
  { label: 'Live median (min)', get: (c) => c.traffic.liveMedianMin, set: tr('liveMedianMin') },
];
const PRICING: Num[] = [
  { label: 'Live $ per hour', get: (c) => c.pricing.livePerH, set: pr('livePerH'), step: 0.01 },
  { label: 'File $ per audio hour', get: (c) => c.pricing.filePerH, set: pr('filePerH'), step: 0.01 },
  { label: 'Minimum billed (s)', get: (c) => c.pricing.minBilledS, set: pr('minBilledS'), hint: 'per transcription' },
];
const WORLD: Num[] = [
  { label: 'Fly boot (s)', get: (c) => c.world.flyBootS, set: wo('flyBootS') },
  { label: 'Pod container (s)', get: (c) => c.world.containerS, set: wo('containerS') },
  { label: 'Pod engine boot (s)', get: (c) => c.world.engineBootS, set: wo('engineBootS') },
  { label: 'Broken hosts', get: (c) => c.world.brokenHostP, set: wo('brokenHostP'), pct: true },
  { label: 'Crashes per pod-hour', get: (c) => c.world.crashPerPodH, set: wo('crashPerPodH'), step: 0.01 },
  { label: 'Failed moves', get: (c) => c.world.moveFailP, set: wo('moveFailP'), pct: true },
  { label: 'Resume finds no GPU', get: (c) => c.world.resumeNoGpuP, set: wo('resumeNoGpuP'), pct: true },
];

export function Fields({ title, items, cfg, set }: { title: string; items: Num[]; cfg: SimConfig; set: (c: SimConfig) => void }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="tbl__h" style={{ marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
        {items.map((n) => {
          const v = n.get(cfg);
          return (
            <label key={n.label} className="field" style={{ margin: 0 }}>
              <span className="field__label">{n.label}{n.pct ? ' (%)' : ''}</span>
              <input className="field__input mono" type="number" step={n.pct ? 1 : n.step ?? 1} value={Number((n.pct ? v * 100 : v).toFixed(4))}
                onChange={(e) => { const x = Number(e.target.value); if (Number.isFinite(x)) set(n.set(cfg, n.pct ? x / 100 : x)); }} />
              {n.hint && <span className="field__hint">{n.hint}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function Settings({ cfg, set, mock, onApply, onRestart, dirty }: { cfg: SimConfig; set: (c: SimConfig) => void; mock: boolean; onApply: () => void; onRestart: () => void; dirty: boolean }) {
  const [more, setMore] = useState(false);
  const breakEven = groupPerH(cfg.balancer) / cfg.balancer.flyMultilingualPerH;
  return (
    <div style={{ display: 'grid', gap: 16, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Toggle on={cfg.balancer.enabled} onChange={(on) => set({ ...cfg, balancer: { ...cfg.balancer, enabled: on } })} label={cfg.balancer.enabled ? 'GPU pool on' : 'GPU pool off: everything on Fly, running pods drain'} />
        <Seg value={cfg.balancer.strategy ?? 'demand'} options={STRATEGIES} onChange={(v) => set({ ...cfg, balancer: { ...cfg.balancer, strategy: v } })} />
        <Toggle on={cfg.balancer.startOnNew} onChange={(on) => set({ ...cfg, balancer: { ...cfg.balancer, startOnNew: on } })}
          label={cfg.balancer.startOnNew ? 'New sessions start pods (faster; a week at 8–120/h keeps 0.4–1.9% less profit)' : 'Only sessions that ran 3 min start pods (slower, a little cheaper)'} />
        <Toggle on={cfg.balancer.pauseFiles} onChange={(on) => set({ ...cfg, balancer: { ...cfg.balancer, pauseFiles: on } })}
          label={cfg.balancer.pauseFiles ? 'Live first: a live session with no place takes a file’s, the file paused (it goes on from its last committed chunk)' : 'A live session with no place is turned away; files are never paused'} />
        <Toggle on={cfg.balancer.podBilledWhileCreating} onChange={(on) => set({ ...cfg, balancer: { ...cfg.balancer, podBilledWhileCreating: on } })}
          label={cfg.balancer.podBilledWhileCreating ? 'Pods bill from their create (a broken host, never given a container, bills nothing)' : 'Pods bill only once their container runs'} />
        <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>
          Break-even {breakEven.toFixed(2)} Multilingual sessions · starts at ≥ {(breakEven + cfg.balancer.startMargin).toFixed(2)} for {cfg.balancer.startWindowS} s, or at once with fewer than {Math.min(Math.floor(cfg.balancer.flyHeadroomShare * cfg.balancer.flyPool), Math.max(cfg.balancer.flyPool - 1, 0))} Fly machines free · drains at ≤ {(breakEven - cfg.balancer.drainMargin).toFixed(2)} for {cfg.balancer.drainWindowS / 60} min
        </span>
      </div>
      <GpuGroups b={cfg.balancer} set={(b) => set({ ...cfg, balancer: b })} />
      <Fields title="GPU POOL" items={POOL} cfg={cfg} set={set} />
      {cfg.balancer.pauseFiles && <Fields title="LIVE FIRST" items={PAUSE} cfg={cfg} set={set} />}
      {mock && <Fields title="MOCK TRAFFIC" items={TRAFFIC} cfg={cfg} set={set} />}
      <Fields title="WHAT CUSTOMERS PAY" items={PRICING} cfg={cfg} set={set} />
      <Fields title="SIMULATED WORLD" items={WORLD} cfg={cfg} set={set} />
      <WorldGpus cfg={cfg} set={set} />
      <div>
        <button type="button" className="btn btn--sm" onClick={() => setMore(!more)}>{more ? 'Hide' : 'Show'} the rules’ timings</button>
      </div>
      {more && <Fields title="RULES" items={RULES} cfg={cfg} set={set} />}
      {more && <Fields title="VOICES WITH NO SESSION" items={SPEECH} cfg={cfg} set={set} />}
      {more && <SpeechBrake cfg={cfg} set={set} />}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn--primary" disabled={!dirty} onClick={onApply} title="The running simulation takes the settings now (recorded, so an export replays it)">Apply now</button>
        {mock && (
          <>
            <label className="field" style={{ margin: 0, width: 110 }}>
              <span className="field__label">Seed</span>
              <input className="field__input mono" type="number" value={cfg.seed} onChange={(e) => set({ ...cfg, seed: Math.max(0, Math.round(Number(e.target.value) || 0)) })} />
            </label>
            <button type="button" className="btn" onClick={onRestart} title="A fresh day from 00:00 with these settings and this seed">Restart with these</button>
          </>
        )}
      </div>
    </div>
  );
}
